// Identity-preserving reconcile for a pushed content doc. Pure — no store, no React, no three.js.
//
// WHY THIS EXISTS. A live content update arrives as freshly-parsed JSON, so every component object is a
// NEW reference even when nothing about it changed. Every renderable subscribes per entity
// (`useDataStore(({componentsById}) => componentsById?.[id])`), and zustand compares by reference — so a
// naive push would re-render and re-apply every entity in the project on every keystroke-level edit.
//
// Reusing the previous object for any id whose value is unchanged means only genuinely edited entities
// re-render. That is what makes "send the whole content.json" cheap enough to prefer over a patch
// protocol: the diffing happens here, where it can't desync, instead of on the wire.

// Type-only: this module must stay runtime-dependency-free so it can be exercised in isolation (the
// component-data-structure barrel pulls in three.js at runtime).
import type { IComponentUnion } from '@r3f-component-data-structure';

export interface IComponentsById {
    [id: string]: IComponentUnion;
}

export interface IReconcileResult {
    /** The map to dispatch: unchanged entries are the PREVIOUS object references. */
    componentsById: IComponentsById;
    /** Ids whose value was identical and whose object reference was therefore preserved. */
    reused: string[];
    /** Ids present in both but with a different value — these will re-render. */
    changed: string[];
    /** Ids new to `next`. */
    added: string[];
    /** Ids present in `prev` but gone from `next`. */
    removed: string[];
}

/**
 * Structural equality for content-doc values (plain JSON: objects, arrays, strings, numbers, booleans,
 * null).
 *
 * Deliberately not `JSON.stringify(a) === JSON.stringify(b)`: that is key-order sensitive, so two
 * semantically identical components serialised in a different order would compare unequal and defeat
 * the whole optimisation. Components are small, so the recursive walk is cheap.
 */
export function deepEqualJson(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;

    const aIsArray = Array.isArray(a);
    if (aIsArray !== Array.isArray(b)) return false;

    if (aIsArray) {
        const aArr = a as unknown[];
        const bArr = b as unknown[];
        if (aArr.length !== bArr.length) return false;
        for (let i = 0; i < aArr.length; i++) {
            if (!deepEqualJson(aArr[i], bArr[i])) return false;
        }
        return true;
    }

    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const aKeys = Object.keys(aObj);
    if (aKeys.length !== Object.keys(bObj).length) return false;
    for (const key of aKeys) {
        if (!Object.prototype.hasOwnProperty.call(bObj, key)) return false;
        if (!deepEqualJson(aObj[key], bObj[key])) return false;
    }
    return true;
}

/**
 * Build the map to dispatch, preserving object identity wherever a component is unchanged.
 *
 * Always keys off the MAP KEY, never `component.id`: many components (`Scene`, `ScreenContent`, …) omit
 * that redundant field entirely, so trusting it would silently mis-handle them.
 */
export function reconcileComponentsById(prev: IComponentsById | null | undefined, next: IComponentsById): IReconcileResult {
    const componentsById: IComponentsById = {};
    const reused: string[] = [];
    const changed: string[] = [];
    const added: string[] = [];
    const removed: string[] = [];

    for (const id in next) {
        const before = prev?.[id];
        if (before === undefined) {
            componentsById[id] = next[id];
            added.push(id);
        } else if (deepEqualJson(before, next[id])) {
            // The whole point: hand back the OLD reference so subscribers don't see a change.
            componentsById[id] = before;
            reused.push(id);
        } else {
            componentsById[id] = next[id];
            changed.push(id);
        }
    }

    if (prev) {
        for (const id in prev) {
            if (!(id in next)) removed.push(id);
        }
    }

    return { componentsById, reused, changed, added, removed };
}
